export function PreparingYourBuilding() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Preparing Your Apartment Building for Sale
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            First impressions matter tremendously when selling an apartment building. Proper preparation can 
            significantly increase your property's value and attract more qualified buyers. Here are essential 
            tips for selling an apartment building that focus on preparation and presentation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/9db8a1e4ec42f1b3b97cf2d49faab4f9.jpg"
              alt="Well-prepared apartment building for sale"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Enhance Curb Appeal and First Impressions</h3>
            <p className="text-gray-600 mb-6">
              The exterior of your apartment building is the first thing potential buyers see, and it sets the tone 
              for their entire evaluation. Investing in curb appeal improvements can yield returns of 100-200% on 
              your investment. Focus on creating a welcoming, well-maintained appearance that suggests the property 
              has been professionally managed and cared for throughout its ownership.
            </p>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-plant-line text-green-600 mr-2"></i>
                  Landscaping and Grounds
                </h4>
                <p className="text-gray-600 text-sm">
                  Maintain pristine landscaping with fresh mulch, trimmed hedges, and seasonal flowers. Ensure 
                  parking lots are clean, striped, and well-lit. Remove any debris, repair cracked pavement, 
                  and power wash walkways and building exteriors.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-paint-brush-line text-orange-600 mr-2"></i>
                  Exterior Maintenance
                </h4>
                <p className="text-gray-600 text-sm">
                  Address any visible exterior issues including peeling paint, damaged siding, broken windows, 
                  or worn roofing. These items signal deferred maintenance to buyers and can significantly 
                  impact their offer price or financing ability.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-door-line text-red-600 mr-2"></i>
                  Common Areas
                </h4>
                <p className="text-gray-600 text-sm">
                  Upgrade common areas including lobbies, hallways, laundry rooms, and amenity spaces. Fresh 
                  paint, modern lighting fixtures, and clean flooring make a significant difference in buyer 
                  perception and can justify higher asking prices.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 rounded-xl p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Essential Documentation to Prepare
          </h3>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            One of the most important tips for selling an apartment building is having comprehensive documentation 
            ready before listing. Organized, detailed records demonstrate professional management and help buyers 
            make confident decisions quickly, leading to faster sales at better prices.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Financial Records</h4>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Last 3 years of profit and loss statements</li>
                <li>• Current rent roll with lease expiration dates</li>
                <li>• Historical occupancy rates and trends</li>
                <li>• Utility expenses and operating costs</li>
                <li>• Property tax records and assessments</li>
                <li>• Insurance policies and claims history</li>
                <li>• Capital expenditure records</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-2xl text-orange-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Property Information</h4>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Complete unit mix and floor plans</li>
                <li>• Property survey and legal description</li>
                <li>• Environmental reports and inspections</li>
                <li>• Roof certification and warranty documents</li>
                <li>• HVAC, plumbing, and electrical system records</li>
                <li>• Recent property appraisals</li>
                <li>• Zoning information and permits</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Tenant Documentation</h4>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Current lease agreements for all units</li>
                <li>• Security deposit records and accounting</li>
                <li>• Tenant payment history and delinquencies</li>
                <li>• Maintenance request logs and responses</li>
                <li>• Tenant turnover rates and reasons</li>
                <li>• Lease renewal rates and trends</li>
                <li>• Any tenant disputes or legal issues</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Address Deferred Maintenance Strategically</h3>
            <p className="text-gray-600 mb-6">
              Among the most valuable tips for selling an apartment building is understanding which repairs to 
              make and which to leave alone. Not all improvements provide equal returns, and some may not be 
              worth the investment if you're selling soon. Focus on repairs that either prevent deal-breakers 
              during inspections or provide strong returns on investment.
            </p>
            <div className="space-y-4">
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">High-Priority Repairs (Must Fix)</h4>
                <p className="text-gray-600 text-sm mb-2">
                  These issues can kill deals or significantly reduce offers. Address them before listing:
                </p>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Roof leaks or structural damage</li>
                  <li>• Code violations or safety hazards</li>
                  <li>• Non-functioning HVAC or plumbing systems</li>
                  <li>• Electrical problems or outdated panels</li>
                  <li>• Foundation issues or water intrusion</li>
                </ul>
              </div>
              <div className="border-l-4 border-orange-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">Medium-Priority Improvements (Consider ROI)</h4>
                <p className="text-gray-600 text-sm mb-2">
                  These improvements can increase value but evaluate cost versus benefit:
                </p>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Unit upgrades in vacant apartments</li>
                  <li>• Common area renovations</li>
                  <li>• Energy efficiency improvements</li>
                  <li>• Amenity additions or upgrades</li>
                </ul>
              </div>
              <div className="border-l-4 border-gray-400 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">Low-Priority Items (Usually Skip)</h4>
                <p className="text-gray-600 text-sm mb-2">
                  These rarely provide sufficient return when selling:
                </p>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Cosmetic upgrades to occupied units</li>
                  <li>• Luxury amenity additions</li>
                  <li>• Major landscaping overhauls</li>
                  <li>• Premium finishes or materials</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <img 
              src="https://readdy.ai/api/search-image?query=professional%20contractor%20inspecting%20apartment%20building%20systems%20checking%20HVAC%20plumbing%20electrical%20maintenance%20worker%20with%20clipboard%20and%20tools%20evaluating%20property%20condition%20quality%20inspection%20commercial%20building%20assessment&width=800&height=600&seq=tips-prep-002&orientation=landscape"
              alt="Property inspection and maintenance"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
}